import { ref } from 'vue';
import { useOrgStore } from '@/stores/org';

/**
 * Process-level cache of qualifier descriptors, keyed by module slug.
 *
 * A descriptor changes only with a deploy or when an admin adds a custom field to
 * the list (each one becomes a filter), so it is fetched once per page load
 * rather than on every mount — a field added in another tab shows up on reload.
 *
 * It holds the request itself, not just its answer: the navbar and the list both
 * ask for the same module as the page mounts, and caching only the answer sent
 * the second request before the first had come back.
 */
const cache = new Map();

function fetchQualifiers(module) {
    return axios.get(`${useOrgStore().api}/search/qualifiers`, { params: { module } }).then(({ data }) => data);
}

function remember(module, request) {
    // A failed load is not remembered: the next mount asks again.
    request.catch(() => cache.delete(module));
    cache.set(module, request);
}

/**
 * Hand over an answer already on its way — the list bootstrap's. It resolves to
 * the descriptor, or to null when the bootstrap left it out, in which case the
 * module's own endpoint is asked as before.
 *
 * @param {string} module
 * @param {Promise<Array|null>} pending
 */
export function primeQualifiers(module, pending) {
    if (!module || cache.has(module)) {
        return;
    }

    remember(
        module,
        pending.then(
            data => data ?? fetchQualifiers(module),
            () => fetchQualifiers(module)
        )
    );
}

/**
 * Forget every descriptor: they carry the organization's custom fields, so the
 * ones read in the organization being left do not describe the next one.
 */
export function clearQualifiers() {
    cache.clear();
}

/** Whether a module's descriptor is already held or on its way. */
export function hasQualifiers(module) {
    return cache.has(module);
}

/**
 * Loads the search qualifier descriptor for a module and resolves entity values
 * for relation qualifiers. The single backend abstraction (`search/resolve`) lets
 * a future search engine replace value lookups without touching the UI.
 */
export function useQualifiers() {
    const orgStore = useOrgStore();

    const qualifiers = ref([]);
    const loading = ref(false);

    async function load(module) {
        if (!module || !orgStore.api) {
            qualifiers.value = [];
            return;
        }

        if (!cache.has(module)) {
            remember(module, fetchQualifiers(module));
        }

        loading.value = true;
        try {
            qualifiers.value = await cache.get(module);
        } catch {
            qualifiers.value = [];
        } finally {
            loading.value = false;
        }
    }

    /**
     * Resolve entities of a given type for relation-qualifier autocomplete.
     *
     * @param {string} type Entity type (e.g. "contacts", "projects").
     * @param {string} query Partial text to match.
     * @param {Object} context Scope of the screen asking — the list's static params.
     *                         An entity narrows its options by the keys it knows
     *                         (a pipeline's stages) and ignores the rest.
     * @returns {Promise<Array<{id: any, label: string, subtitle: ?string}>>}
     */
    async function resolve(type, query, context = {}) {
        if (!type || !orgStore.api) {
            return [];
        }

        try {
            const { data } = await axios.get(`${orgStore.api}/search/resolve`, {
                params: { type, q: query || '', context }
            });
            return data;
        } catch {
            return [];
        }
    }

    /**
     * Reverse-resolve known entity ids to their labels (e.g. to render qualifier chips).
     *
     * @param {string} type Entity type (e.g. "contacts", "projects").
     * @param {Array<string|number>} ids
     * @returns {Promise<Array<{id: any, label: string, subtitle: ?string}>>}
     */
    async function resolveByIds(type, ids) {
        if (!type || !ids || !ids.length || !orgStore.api) {
            return [];
        }

        try {
            const { data } = await axios.get(`${orgStore.api}/search/resolve`, {
                params: { type, ids: ids.join(',') }
            });
            return data;
        } catch {
            return [];
        }
    }

    return { qualifiers, loading, load, resolve, resolveByIds };
}
